import React, { useEffect } from 'react';

import { Box, Button, Grid } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { ShowErrors } from 'common/components';
import {
    ReactHookFormIntlNumberInput,
    ReactHookFormSelect,
} from 'common/components/form/RHF';
import yupResolver from 'common/utils/yupResolver';

import * as actions from '../actions';
import * as selectors from '../selectors';
import validate from '../validate';

const ProductForm = ({
    cities,
    countries,
    errors,
    fetchPolitcalDivisions1,
    fetchPolitcalDivisions2,
    filterData,
    handleClickCloseForm,
    handleOnSave,
    initialValues,
    isFetchingDetail,
    isSaving,
    states,
}) => {
    const { t } = useTranslation();

    const { control, handleSubmit, reset, setValue, watch } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
    });

    useEffect(() => {
        return reset(initialValues);
    }, [initialValues]);

    const [country, state] = watch(['country', 'state']);

    useEffect(() => {
        if (!country) return;
        fetchPolitcalDivisions1({ country });
    }, [country]);

    useEffect(() => {
        if (!country || !state) return;
        fetchPolitcalDivisions2({ country, political_division: state });
    }, [state]);

    const handleClickSave = (values) => handleOnSave(filterData, values);

    const handleOnChangeCountry = () => {
        setValue('state', '');
        setValue('city', '');
    };

    const handleOnChangeState = () => {
        setValue('city', '');
    };

    const isLoading = isFetchingDetail || isSaving;

    return (
        <form onSubmit={handleSubmit(handleClickSave)}>
            <Box sx={{ mb: 2 }}>
                <ReactHookFormSelect
                    control={control}
                    label={t('Country')}
                    name="country"
                    onChange={handleOnChangeCountry}
                    options={countries}
                />
            </Box>

            <Box sx={{ mb: 2 }}>
                <ReactHookFormSelect
                    control={control}
                    label={t('State')}
                    name="state"
                    options={states}
                    onChange={handleOnChangeState}
                />
            </Box>

            <Box sx={{ mb: 2 }}>
                <ReactHookFormSelect
                    control={control}
                    label={t('City')}
                    name="city"
                    options={cities}
                />
            </Box>

            <Box>
                <ReactHookFormIntlNumberInput
                    append="km/h"
                    control={control}
                    disabled={isLoading}
                    fullWidth
                    label={t('Wind speed')}
                    name="wind_speed"
                    placeholder={t('Wind speed')}
                    required
                />
            </Box>

            <ShowErrors
                errors={errors}
                mt={2}
                severity="error"
                variant="filled"
            />

            <Grid container mt={2}>
                <Grid
                    size="grow"
                    sx={{
                        display: 'flex',
                        flexDirection: {
                            md: 'row',
                            xs: 'column',
                        },
                        gap: 2,
                        justifyContent: {
                            md: 'right',
                            xs: 'center',
                        },
                    }}
                >
                    <Button
                        color="secondary"
                        disabled={isSaving}
                        onClick={handleClickCloseForm}
                        sx={{
                            width: { xs: '100%', md: 'auto' },
                            order: { xs: 2, md: 1 },
                        }}
                        variant="text"
                    >
                        {t('Cancel')}
                    </Button>

                    <Button
                        disabled={isLoading}
                        sx={{
                            width: { xs: '100%', md: 'auto' },
                            order: { xs: 1, md: 2 },
                            mb: { xs: 2, md: 0 },
                        }}
                        type="submit"
                        variant="outlined"
                    >
                        {isSaving ? t('Saving').concat('...') : t('Save')}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

ProductForm.propTypes = {
    cities: PropTypes.array,
    countries: PropTypes.array,
    errors: PropTypes.array,
    fetchPolitcalDivisions1: PropTypes.func,
    fetchPolitcalDivisions2: PropTypes.func,
    filterData: PropTypes.object,
    handleClickCloseForm: PropTypes.func,
    handleOnSave: PropTypes.func,
    initialValues: PropTypes.object,
    isFetchingDetail: PropTypes.bool,
    isSaving: PropTypes.bool,
    states: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
    cities: selectors.getCitiesToSelect,
    errors: selectors.getErrorsSave,
    initialValues: selectors.getInitialValues,
    isFetchingDetail: selectors.getIsFetchingItemDetail,
    isNewRecord: selectors.getIsNewRecord,
    isSaving: selectors.getIsSaving,
    states: selectors.getStatesToSelect,
});

const mapDispatchToProps = (dispatch) => ({
    fetchPolitcalDivisions1: (values) =>
        dispatch(actions.fetchPolitcalDivisions1(values)),
    fetchPolitcalDivisions2: (values) =>
        dispatch(actions.fetchPolitcalDivisions2(values)),
    handleOnSave: (filterData, values) =>
        dispatch(actions.save(filterData, values)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductForm);
